/**
 * tool-more-menu.ts — the (⋮) overflow menu on the public tool page header.
 *
 * Mirrors the build-on-demand popover lifecycle of share.ts (single open
 * instance, document-click + Escape dismiss) but holds the secondary actions
 * that don't belong in Share or the heart/pin buttons:
 *
 *   - Embed on your website → a dialog with a copyable <iframe> snippet that
 *     points at /tools/{slug}/embed (the chrome-free runtime). This is the
 *     growth loop: every embed is a backlink + brand impression.
 *   - Show QR code → a dialog rendering a scannable QR of the canonical tool
 *     URL for the "open on phone" hand-off.
 *   - Report a problem → deep-links to the existing Feedback form with the Bug
 *     Report category preselected and the tool path prefilled.
 *
 * Everything is built on demand and uses textContent / element APIs (never
 * innerHTML with page-derived strings) so there is no injection surface.
 */
import { devLog } from "./dev-utils.js";
import { toast } from "../components/toast.js";
import { shareOrPopover } from "./share.js";
import { setIcon } from "./icon-svg.js";
import { showFormModal } from "../components/modal.js";
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
function personalizationSync() {
    const api = window
        .PersonalizationSync;
    return api ?? null;
}
function resolveData(trigger) {
    const slug = (trigger.dataset["toolSlug"] ?? "").trim();
    const title = (trigger.dataset["toolTitle"] ?? "").trim() || slug;
    const canonicalUrl = (trigger.dataset["canonicalUrl"] ?? "").trim() ||
        `${window.location.origin}/tools/${slug}`;
    const embedUrl = `${window.location.origin}/tools/${slug}/embed`;
    return { slug, title, canonicalUrl, embedUrl };
}
function metaDescription() {
    const meta = document.querySelector('meta[name="description"]');
    return meta?.content ?? "";
}
// HTML-attribute-safe escaping for the copy-paste embed snippet so a tool
// title containing quotes/angle brackets can't break the pasted markup.
function escapeForAttribute(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
function buildEmbedSnippet(data) {
    const safeTitle = escapeForAttribute(`${data.title} — ToolNexus`);
    return (`<iframe src="${data.embedUrl}" width="100%" height="640" ` +
        `loading="lazy" style="border:0;border-radius:12px" ` +
        `title="${safeTitle}"></iframe>`);
}
/* ── Popover (the menu itself) ─────────────────────────────────────────── */
let openMenu = null;
let menuTrigger = null;
function closeMenu() {
    if (openMenu === null)
        return;
    openMenu.remove();
    openMenu = null;
    if (menuTrigger !== null) {
        menuTrigger.setAttribute("aria-expanded", "false");
        menuTrigger = null;
    }
    document.removeEventListener("click", onDocumentClick, true);
    document.removeEventListener("keydown", onMenuKeydown, true);
}
function onDocumentClick(ev) {
    if (openMenu !== null && !openMenu.contains(ev.target)) {
        closeMenu();
    }
}
function onMenuKeydown(ev) {
    if (ev.key === "Escape") {
        closeMenu();
        menuTrigger?.focus();
    }
}
function addMenuItem(menu, icon, label, onActivate) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "tn-more-menu__item";
    button.setAttribute("role", "menuitem");
    const iconEl = document.createElement("span");
    iconEl.className = "material-symbols-outlined";
    iconEl.setAttribute("aria-hidden", "true");
    setIcon(iconEl, icon);
    const labelEl = document.createElement("span");
    labelEl.textContent = label;
    button.append(iconEl, labelEl);
    button.addEventListener("click", () => {
        closeMenu();
        onActivate();
    });
    menu.appendChild(button);
}
function buildMenu(trigger, data) {
    const menu = document.createElement("div");
    menu.className = "tn-more-menu";
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", t("js.toolmenu.moreOptions", "More options"));
    // Favorites and shortcuts were merged into one "Saved tools" concept; the
    // single Save control lives in the title bar ([data-tn-favorite]), so the
    // former shortcut proxy menu item was removed.
    // Share reuses the full share.ts flow (native sheet → static intent popover)
    // anchored to the ⋮ trigger's parent so it lands beside the header actions.
    addMenuItem(menu, "share", t("js.toolmenu.share", "Share"), () => {
        void shareOrPopover(trigger.parentElement ?? document.body, {
            url: data.canonicalUrl,
            title: data.title,
            text: metaDescription() || data.title,
        });
    });
    // Add to collection — only for signed-in users (the endpoints 401 otherwise).
    if (personalizationSync()?.enabled === true) {
        addMenuItem(menu, "folder", t("js.toolmenu.addToCollection", "Add to collection"), () => openAddToCollectionDialog(data));
    }
    addMenuItem(menu, "code", t("js.toolmenu.embedOnWebsite", "Embed on your website"), () => openEmbedDialog(data));
    addMenuItem(menu, "qr_code_2", t("js.toolmenu.showQrCode", "Show QR code"), () => openQrDialog(data));
    // Report routes to the existing Feedback form (Bug Report preselected, tool
    // path prefilled). A real anchor so it behaves like a normal navigation.
    const report = document.createElement("a");
    report.className = "tn-more-menu__item";
    report.setAttribute("role", "menuitem");
    const params = new URLSearchParams({
        category: "Bug Report",
        context: `/tools/${data.slug}`,
    });
    report.href = `/feedback?${params.toString()}`;
    const reportIcon = document.createElement("span");
    reportIcon.className = "material-symbols-outlined";
    reportIcon.setAttribute("aria-hidden", "true");
    setIcon(reportIcon, "flag");
    const reportLabel = document.createElement("span");
    reportLabel.textContent = t("js.toolmenu.reportProblem", "Report a problem");
    report.append(reportIcon, reportLabel);
    report.addEventListener("click", () => {
        closeMenu();
    });
    menu.appendChild(report);
    return menu;
}
function toggleMenu(trigger) {
    if (openMenu !== null) {
        closeMenu();
        return;
    }
    const data = resolveData(trigger);
    if (data.slug === "")
        return;
    const menu = buildMenu(trigger, data);
    const host = trigger.parentElement ?? document.body;
    if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
    }
    host.appendChild(menu);
    openMenu = menu;
    menuTrigger = trigger;
    trigger.setAttribute("aria-expanded", "true");
    // Defer listener attach so this same click doesn't immediately close it.
    window.setTimeout(() => {
        document.addEventListener("click", onDocumentClick, true);
        document.addEventListener("keydown", onMenuKeydown, true);
        menu.querySelector('[role="menuitem"]')?.focus();
    }, 0);
}
/* ── Modal dialogs (embed + QR) ────────────────────────────────────────── */
let openDialog = null;
let dialogReturnFocus = null;
function closeDialog() {
    if (openDialog === null)
        return;
    openDialog.remove();
    openDialog = null;
    document.removeEventListener("keydown", onDialogKeydown, true);
    dialogReturnFocus?.focus();
    dialogReturnFocus = null;
}
function onDialogKeydown(ev) {
    if (ev.key === "Escape")
        closeDialog();
}
// Builds the backdrop + dialog shell with a titled header and close button.
// Callers fill the returned body element with their own content.
function buildDialogShell(titleText) {
    const backdrop = document.createElement("div");
    backdrop.className = "tn-dialog-backdrop";
    backdrop.addEventListener("click", (ev) => {
        if (ev.target === backdrop)
            closeDialog();
    });
    const dialog = document.createElement("div");
    dialog.className = "tn-dialog";
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", titleText);
    const header = document.createElement("div");
    header.className = "tn-dialog__header";
    const title = document.createElement("h2");
    title.className = "tn-dialog__title";
    title.textContent = titleText;
    const close = document.createElement("button");
    close.type = "button";
    close.className = "tn-dialog__close";
    close.setAttribute("aria-label", t("js.toolmenu.close", "Close"));
    const closeIcon = document.createElement("span");
    closeIcon.className = "material-symbols-outlined";
    closeIcon.setAttribute("aria-hidden", "true");
    setIcon(closeIcon, "close");
    close.appendChild(closeIcon);
    close.addEventListener("click", closeDialog);
    header.append(title, close);
    const body = document.createElement("div");
    body.className = "tn-dialog__body";
    dialog.append(header, body);
    backdrop.appendChild(dialog);
    return { backdrop, body };
}
function presentDialog(backdrop, focusTarget) {
    if (openDialog !== null)
        closeDialog();
    dialogReturnFocus = document.activeElement ?? menuTrigger;
    document.body.appendChild(backdrop);
    openDialog = backdrop;
    document.addEventListener("keydown", onDialogKeydown, true);
    focusTarget?.focus();
}
function openEmbedDialog(data) {
    const { backdrop, body } = buildDialogShell(t("js.toolmenu.embedOnWebsite", "Embed on your website"));
    const hint = document.createElement("p");
    hint.className = "tn-dialog__hint";
    hint.textContent = t("js.toolmenu.embedHint", "Paste this snippet into any page to embed this tool. Visitors run it inline — no account needed.");
    const snippet = buildEmbedSnippet(data);
    const textarea = document.createElement("textarea");
    textarea.className = "tn-embed-snippet";
    textarea.readOnly = true;
    textarea.rows = 4;
    textarea.value = snippet;
    textarea.setAttribute("aria-label", t("js.toolmenu.embedCode", "Embed code"));
    textarea.addEventListener("focus", () => textarea.select());
    const actions = document.createElement("div");
    actions.className = "tn-dialog__actions";
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "tn-dialog__btn";
    copyBtn.textContent = t("js.toolmenu.copyCode", "Copy code");
    copyBtn.addEventListener("click", () => {
        void copyText(snippet, textarea);
    });
    actions.appendChild(copyBtn);
    body.append(hint, textarea, actions);
    presentDialog(backdrop, copyBtn);
}
// Local QR rendering via the vendored qr-code-styling.min.js. The library is
// loaded on demand (matches the qr-generator tool's pattern) and appended as a
// <canvas>, so no third party ever learns the URL being encoded — important on a
// page whose "100% Local" strip would otherwise contradict itself the moment a
// visitor opens the QR dialog.
let qrLibPromise = null;
function loadQrLib() {
    if (qrLibPromise !== null)
        return qrLibPromise;
    qrLibPromise = new Promise((resolve) => {
        if (window.QRCodeStyling) {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = "/js/vendor/qr-code-styling.min.js";
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
    });
    return qrLibPromise;
}
function renderLocalQr(host, url) {
    void loadQrLib().then(() => {
        const Ctor = window.QRCodeStyling;
        if (!Ctor)
            return;
        host.innerHTML = "";
        const qr = new Ctor({
            width: 220,
            height: 220,
            type: "canvas",
            data: url,
            margin: 0,
            qrOptions: { errorCorrectionLevel: "M" },
            dotsOptions: { type: "rounded", color: "#0f172a" },
            cornersSquareOptions: { type: "extra-rounded", color: "#0f172a" },
            cornersDotOptions: { type: "dot", color: "#0f172a" },
            backgroundOptions: { color: "#ffffff" },
        });
        qr.append(host);
    });
}
function openQrDialog(data) {
    const { backdrop, body } = buildDialogShell(t("js.toolmenu.qrTitle", "Scan to open on your phone"));
    const figure = document.createElement("figure");
    figure.className = "tn-qr-figure";
    // QR is rendered locally via the vendored qr-code-styling.min.js (the same
    // library the qr-generator tool uses) so the public tool page — whose trust
    // strip promises "100% Local" for browser-run tools — never phones a third
    // party to draw a code. The figure keeps a fallback <img> hidden until the
    // canvas renders; if the library fails to load, the URL caption below still
    // lets the user reach the page.
    const qrHost = document.createElement("div");
    qrHost.className = "tn-qr-figure__canvas";
    qrHost.setAttribute("role", "img");
    qrHost.setAttribute("aria-label", t("js.toolmenu.qrAlt", "QR code linking to {0}").replace("{0}", data.title));
    const caption = document.createElement("figcaption");
    caption.textContent = data.canonicalUrl;
    figure.append(qrHost, caption);
    void renderLocalQr(qrHost, data.canonicalUrl);
    const actions = document.createElement("div");
    actions.className = "tn-dialog__actions";
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "tn-dialog__btn tn-dialog__btn--ghost";
    copyBtn.textContent = t("js.toolmenu.copyLink", "Copy link");
    copyBtn.addEventListener("click", () => {
        void copyText(data.canonicalUrl, null);
    });
    actions.appendChild(copyBtn);
    body.append(figure, actions);
    presentDialog(backdrop, copyBtn);
}
function openAddToCollectionDialog(data) {
    const sync = personalizationSync();
    if (sync === null)
        return;
    const { backdrop, body } = buildDialogShell(t("js.toolmenu.addToCollection", "Add to collection"));
    const hint = document.createElement("p");
    hint.className = "tn-dialog__hint";
    hint.textContent = t("js.toolmenu.addToCollectionHint", 'Add "{0}" to one of your collections, or create a new one.').replace("{0}", data.title);
    body.appendChild(hint);
    const list = document.createElement("div");
    list.className = "tn-collection-picker";
    list.textContent = t("js.toolmenu.loadingCollections", "Loading collections…");
    body.appendChild(list);
    const addTo = (collectionId, name) => {
        void sync.addToCollection(collectionId, data.slug).then((ok) => {
            if (ok) {
                toast.show({ message: t("js.toolmenu.addedTo", 'Added to "{0}"').replace("{0}", name), type: "success" });
                closeDialog();
            }
            else {
                toast.show({ message: t("js.toolmenu.couldNotAdd", "Could not add to collection"), type: "error" });
            }
        });
    };
    const renderCollections = (collections) => {
        list.textContent = "";
        if (collections.length === 0) {
            const empty = document.createElement("p");
            empty.className = "tn-dialog__hint";
            empty.textContent = t("js.toolmenu.noCollections", "No collections yet. Create your first one below.");
            list.appendChild(empty);
        }
        else {
            for (const c of collections) {
                const item = document.createElement("button");
                item.type = "button";
                item.className = "tn-collection-picker__item";
                const label = document.createElement("span");
                label.textContent = c.name;
                const count = document.createElement("span");
                count.className = "tn-collection-picker__count";
                count.textContent =
                    c.itemCount === 1
                        ? t("js.toolmenu.toolCountOne", "{0} tool").replace("{0}", String(c.itemCount))
                        : t("js.toolmenu.toolCountOther", "{0} tools").replace("{0}", String(c.itemCount));
                item.append(label, count);
                item.addEventListener("click", () => addTo(c.id, c.name));
                list.appendChild(item);
            }
        }
        const createRow = document.createElement("div");
        createRow.className = "tn-dialog__actions";
        const input = document.createElement("input");
        input.type = "text";
        input.className = "tn-collection-picker__input";
        input.placeholder = t("js.toolmenu.newCollectionName", "New collection name");
        input.maxLength = 100;
        input.setAttribute("aria-label", t("js.toolmenu.newCollectionName", "New collection name"));
        const createBtn = document.createElement("button");
        createBtn.type = "button";
        createBtn.className = "tn-dialog__btn";
        createBtn.textContent = t("js.toolmenu.createAndAdd", "Create & add");
        const submit = () => {
            const name = input.value.trim();
            if (name === "")
                return;
            createBtn.disabled = true;
            void sync.createCollection(name).then((res) => {
                if (res !== null) {
                    addTo(res.collectionId, res.name);
                }
                else {
                    createBtn.disabled = false;
                    toast.show({ message: t("js.toolmenu.couldNotCreate", "Could not create collection"), type: "error" });
                }
            });
        };
        createBtn.addEventListener("click", submit);
        input.addEventListener("keydown", (ev) => {
            if (ev.key === "Enter") {
                ev.preventDefault();
                submit();
            }
        });
        createRow.append(input, createBtn);
        list.appendChild(createRow);
    };
    void sync.listCollections().then((res) => {
        renderCollections(res?.collections ?? []);
    });
    presentDialog(backdrop, null);
}
async function copyText(text, selectable) {
    const writer = navigator.clipboard?.writeText;
    if (typeof writer === "function") {
        try {
            await writer.call(navigator.clipboard, text);
            toast.show({ message: t("js.toolmenu.copiedToClipboard", "Copied to clipboard"), type: "success" });
            return;
        }
        catch {
            /* fall through to manual selection */
        }
    }
    if (selectable !== null) {
        selectable.focus();
        selectable.select();
    }
    await showFormModal({
        title: t("js.toolmenu.copyThis", "Copy this"),
        submitText: t("js.toolmenu.done", "Done"),
        cancelText: t("js.toolmenu.closeAction", "Close"),
        fields: [{ name: "text", label: t("js.toolmenu.textLabel", "Text"), type: "text", value: text }],
    });
}
/* ── Bootstrap ─────────────────────────────────────────────────────────── */
function init() {
    const trigger = document.querySelector("[data-tn-more]");
    if (trigger === null)
        return;
    if (trigger.dataset["tnMoreBound"] === "1")
        return;
    trigger.dataset["tnMoreBound"] = "1";
    devLog("tool-more-menu", "Initializing more menu");
    trigger.addEventListener("click", (ev) => {
        ev.preventDefault();
        toggleMenu(trigger);
    });
}
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    }
    else {
        init();
    }
}
//# sourceMappingURL=tool-more-menu.js.map